import {Component, ElementRef, HostListener, OnInit, ViewChild} from '@angular/core';
import {Application} from '../../../model/application';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {MessageService} from 'primeng/api';
import {TranslocoService} from '@ngneat/transloco';
import {ApplicationService} from '../../../services/application.service';
import {ComponentCanDeactivate} from '../../../common/can-deactivate/component-can-deactivate';

@Component({
  selector: 'app-applications-form',
  templateUrl: './applications-form.component.html',
  styleUrls: ['./applications-form.component.scss']
})
export class ApplicationsFormComponent implements OnInit, ComponentCanDeactivate {

  maxFileSizeKB: number = 250;

  application: Application;
  applicationForm: FormGroup;
  persistingChanges: any;
  maxSizeErrorMessage: string;
  successEnd: boolean = false;

  public uploadedFiles: any[] = [];
  public image: File = null;
  public imageHex: string;

  public color: string;


  @ViewChild('fileUploader', {static: false})
  fileInput: ElementRef;

  mode: 'create' | 'edit';

  constructor(private formBuilder: FormBuilder,
              private router: Router,
              private route: ActivatedRoute,
              private messageService: MessageService,
              private applicationService: ApplicationService,
              private translocoService: TranslocoService) {
  }

  ngOnInit(): void {
    this.route.url.subscribe(urlSegment => {
      let firstPath = urlSegment[0].path;
      if (firstPath === 'new') {
        this.mode = 'create';
        this.loadCreationForm();
      } else {
        this.mode = 'edit';
        this.loadEditionForm(firstPath);
      }
    });
  }

  private loadCreationForm(): void {
    this.application = new Application();
    this.application.color = '#2196F3';
    this.createForm(this.application);
  }

  private createForm(application: Application): void {
    this.color = application.color;
    this.applicationForm = this.formBuilder.group({
      'code': [application.code, [Validators.required, Validators.maxLength(255)]],
      'name': [application.name, [Validators.required, Validators.maxLength(255)]],
      'description': [application.description, [Validators.maxLength(255)]],
      'url': [application.url, [Validators.required, Validators.maxLength(255)]],
      'color': [application.color, [Validators.required]]
    });
  }

  loadEditionForm(uuid) {
    this.applicationService.getApplication(uuid).then((response) => {
      this.application = response.applicationPersistData;
      this.createForm(this.application);
      this.applicationForm.addControl('uuid', new FormControl(this.application.uuid, [Validators.required, Validators.maxLength(36)]));
      this.imageHex = 'data:image/png;base64,' + this.application.icon;
      this.image = this.base64ToFile(this.application.icon);
    });
  }

  public getIcon(): string {
    return this.imageHex;
  }

  public dispatchClick() {
    this.fileInput.nativeElement.click();
  }

  public fileChangeEvent(filesInput: any) {
    if (filesInput.target.files[0]) {
      if (filesInput.target.files[0].size <= this.maxFileSizeKB * 1024) {
        this.maxSizeErrorMessage = '';
        this.image = filesInput.target.files[0];
        this.fileToBase64(this.image).then((response) => {
          this.imageHex = response.toLocaleString();
        });
      } else {
        this.maxSizeErrorMessage = this.maxFileSizeKB + ' KB.';
      }
    }
  }

  private base64ToFile(imageHex: string): File {
    // convert base64 to Blob
    const byteCharacters = atob(imageHex);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {type: 'image/png'});
    return <File>blob; // cast to File
  }

  private fileToBase64(imageToConvert: File) {
    return (new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(imageToConvert);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      })
    );
  }

  public removeImage() {
    this.image = null;
    this.fileInput.nativeElement.value = null;
  }

  public isValidFile(): boolean {
    return this.image !== null;
  }

  persistAssetData() {
    if (!this.applicationForm.valid || !this.isValidFile()) {
      return;
    }

    this.persistingChanges = true;
    this.save();
  }

  public save() {
    const formData: FormData = new FormData();
    formData.append('uploadingFiles', this.image);
    formData.append('applicationData', JSON.stringify(this.applicationForm.getRawValue()));
    let a = formData.get('applicationData');
    let b = formData.get('uploadingFiles');

    let promise;
    if (this.mode == 'create') {
      promise = this.applicationService.createApplication(formData);
    } else {
      promise = this.applicationService.editApplication(formData, this.application.uuid);
    }

    promise.then(() => {
      this.messageService.add({
        severity: 'success',
        summary: this.translocoService.translate(`forms.application.success.${this.mode}.summary`),
        detail: this.translocoService.translate(`forms.application.success.${this.mode}.detail`),
        sticky: false
      });

      this.successEnd = true;
      this.router.navigate([`/applications`]);
    }).catch(error => {
      this.messageService.add({
        severity: 'error',
        summary: this.translocoService.translate(`forms.application.error.${this.mode}.summary`),
        detail: this.translocoService.translate(`forms.application.error.${this.mode}.detail`),
        sticky: false
      });
      this.persistingChanges = false;
    });
  }

  cancelAction() {
    return this.router.navigate([`/applications`]);
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.successEnd || this.applicationForm.untouched;
  }

}

